/**
 * Editorial hero — default state for the football-team adaptive hero.
 *
 * Renders the tenant's latest featured post through the shared sports-ui
 * ContentCard (hero variant) — a 16:9 image card with overlay title and
 * gradient. Falls back to a themed placeholder card when no post is available.
 */

"use client";

import { ContentCard, type ImageComponent } from "fansunited-sports-ui";
import { FillImage } from "@/components/ui/FillImage";
import { MediaVariantsProvider } from "@/components/ui/MediaVariantsProvider";
import { getPostImageUrl } from "@/components/ui/PostImage";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { Post, Tenant } from "@/payload-types";
import type { LocaleCode } from "@/lib/labels/types";

interface EditorialHeroProps {
    featuredPost?: Post;
    tenant: Tenant;
    locale: LocaleCode;
    /** Hero image is the page's LCP candidate: eager load + `fetchpriority="high"`. Only the first hero on a page. */
    priority?: boolean;
}

export function EditorialHero({ featuredPost, tenant, priority = false }: EditorialHeroProps) {
    const nav = useNavigation();
    const labels = useLabels();

    if (!featuredPost) {
        return (
            <ContentCard
                content={{
                    id: `hero-fallback-${tenant.id ?? "tenant"}`,
                    type: "article",
                    title: labels.adaptiveHero.welcomeTitle,
                    subtitle: tenant.name,
                }}
                variant="hero"
                titleAs="h1"
                styles={{
                    shadow: "none",
                    borderRadius: "lg",
                    borderWidth: "0",
                    typography: {
                        title: "text-3xl md:text-5xl font-extrabold leading-tight",
                        subtitle: "text-xs uppercase tracking-widest opacity-80",
                    },
                }}
            />
        );
    }

    return (
        <MediaVariantsProvider media={featuredPost.heroImage} sizes="100vw" priority={priority}>
            <ContentCard
                content={{
                    id: String(featuredPost.id),
                    type: "article",
                    link: nav.post(featuredPost),
                    title: featuredPost.title,
                    description: featuredPost.summary || undefined,
                    image: {
                        url: getPostImageUrl(featuredPost, "xlarge"),
                        alt: featuredPost.title,
                    },
                    gradient: "linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.6) 25%, transparent 55%)",
                }}
                variant="hero"
                titleAs="h1"
                ImageComponent={FillImage as ImageComponent}
                styles={{
                    shadow: "none",
                    borderRadius: "lg",
                    borderWidth: "0",
                    typography: {
                        title: "text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight mb-2!",
                        description: "text-sm md:text-base text-white/80 line-clamp-2 !hidden md:!block",
                    },
                }}
            />
        </MediaVariantsProvider>
    );
}
